import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Lock, ChevronDown } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const LINKS_A = [
  { to: "/master-plan", label: "Master Plan" },
];
const STAGES = [
  { to: "/stages", label: "All Stages" },
  { to: "/available-land", label: "Available Land" },
];
const LINKS_MID = [
  { to: "/builders", label: "Builders" },
  { to: "/commercial", label: "Commercial" },
  { to: "/apartment", label: "Apartment" },
  { to: "/videos", label: "Videos" },
];
const COMMUNITY = [
  { to: "/about", label: "About" },
  { to: "/community", label: "Overview" },
  { to: "/community/local-businesses", label: "Local Businesses" },
  { to: "/community/parks-and-amenities", label: "Parks & Everyday Life" },
  { to: "/community/getting-around", label: "Getting Around" },
  { to: "/location", label: "Location" },
];
const LINKS_B = [
  { to: "/contact", label: "Contact" },
];

const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();
  const aboutActive = COMMUNITY.some((a) => loc.pathname === a.to);
  const stagesActive = STAGES.some((a) => loc.pathname === a.to) || loc.pathname.startsWith("/stages");

  const linkCls = (active) =>
    `link-underline text-[0.8rem] tracking-wide transition-colors ${active ? "text-[#C9B79C]" : "text-[#F3EEE3]/70 hover:text-[#F3EEE3]"}`;

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#12201A]/92 backdrop-blur-md border-b border-[#ffffff14]">
      <div className="bg-[#C9B79C] text-[#12201A] text-center text-[0.66rem] md:text-[0.72rem] font-medium tracking-wide px-4 py-1.5 leading-tight" data-testid="builders-only-notice">
        Lots at Deer Park Estates are available to registered builders only — not for sale to private individuals.
      </div>
      <div className="max-w-[1500px] mx-auto px-5 md:px-8 h-[64px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
          <img src="/deer-logo.png" alt="Deer Park Estates logo" className="h-10 w-auto" width="35" height="40" />
          <span className="flex flex-col leading-none">
            <span className="font-display text-[1.35rem] tracking-tight text-[#F3EEE3]">Deer Park</span>
            <span className="eyebrow text-[0.5rem] text-[#F3EEE3]/55 mt-1">Estates · Spruce Grove</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {LINKS_A.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`} className={linkCls(loc.pathname === l.to)}>
              {l.label}
            </Link>
          ))}

          {/* Stages dropdown */}
          <div className="relative" onMouseEnter={() => setStagesOpen(true)} onMouseLeave={() => setStagesOpen(false)}>
            <button
              type="button"
              data-testid="nav-stages-menu"
              onClick={() => setStagesOpen((o) => !o)}
              className={`inline-flex items-center gap-1 ${linkCls(stagesActive)}`}
            >
              Stages <ChevronDown size={13} className={`transition-transform ${stagesOpen ? "rotate-180" : ""}`} />
            </button>
            <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 ${stagesOpen ? "block" : "hidden"}`}>
              <div className="min-w-[180px] rounded-xl border border-[#ffffff1a] bg-[#0F1B15] shadow-2xl p-2" data-testid="stages-menu">
                {STAGES.map((a) => (
                  <Link
                    key={a.to}
                    to={a.to}
                    data-testid={`nav-${a.label.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setStagesOpen(false)}
                    className={`block px-4 py-2.5 rounded-lg text-[0.82rem] transition-colors ${loc.pathname === a.to ? "text-[#C9B79C] bg-[#C9B79C]/10" : "text-[#F3EEE3]/75 hover:text-[#F3EEE3] hover:bg-[#F3EEE3]/[0.05]"}`}
                  >
                    {a.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {LINKS_MID.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`} className={linkCls(loc.pathname === l.to)}>
              {l.label}
            </Link>
          ))}

          {/* About dropdown */}
          <div className="relative" onMouseEnter={() => setAboutOpen(true)} onMouseLeave={() => setAboutOpen(false)}>
            <button
              type="button"
              data-testid="nav-community-menu"
              onClick={() => setAboutOpen((o) => !o)}
              className={`inline-flex items-center gap-1 ${linkCls(aboutActive)}`}
            >
              Community <ChevronDown size={13} className={`transition-transform ${aboutOpen ? "rotate-180" : ""}`} />
            </button>
            <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 ${aboutOpen ? "block" : "hidden"}`}>
              <div className="min-w-[180px] rounded-xl border border-[#ffffff1a] bg-[#0F1B15] shadow-2xl p-2" data-testid="about-menu">
                {COMMUNITY.map((a) => (
                  <Link
                    key={a.to}
                    to={a.to}
                    data-testid={`nav-${a.label.toLowerCase()}`}
                    onClick={() => setAboutOpen(false)}
                    className={`block px-4 py-2.5 rounded-lg text-[0.82rem] transition-colors ${loc.pathname === a.to ? "text-[#C9B79C] bg-[#C9B79C]/10" : "text-[#F3EEE3]/75 hover:text-[#F3EEE3] hover:bg-[#F3EEE3]/[0.05]"}`}
                  >
                    {a.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {LINKS_B.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`} className={linkCls(loc.pathname === l.to)}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to={user ? (user.role === "admin" ? "/admin" : "/portal") : "/login"}
            data-testid="investor-cta"
            className="hidden sm:inline-flex items-center gap-2 text-[0.78rem] tracking-wide border border-[#C9B79C]/45 text-[#F3EEE3] px-4 py-2 rounded-full hover:bg-[#C9B79C] hover:text-[#12201A] transition-all"
          >
            <Lock size={13} /> {user ? (user.role === "admin" ? "Admin" : "Builders Portal") : "Builders Login"}
          </Link>
          <button
            className="lg:hidden text-[#F3EEE3]"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#ffffff14] bg-[#12201A] px-5 py-4 flex flex-col gap-3">
          {LINKS_A.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-[#F3EEE3]/80 text-sm">{l.label}</Link>
          ))}
          <div className="pt-2 mt-1 border-t border-[#ffffff14]">
            <p className="eyebrow text-[0.5rem] text-[#C9B79C] mb-2">Stages</p>
            <div className="flex flex-col gap-3 pl-3">
              {STAGES.map((a) => (
                <Link key={a.to} to={a.to} onClick={() => setOpen(false)} data-testid={`mnav-${a.label.toLowerCase().replace(/\s/g, "-")}`} className="text-[#F3EEE3]/80 text-sm">{a.label}</Link>
              ))}
            </div>
          </div>
          <div className="pt-2 mt-1 border-t border-[#ffffff14] flex flex-col gap-3">
            {LINKS_MID.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-[#F3EEE3]/80 text-sm">{l.label}</Link>
            ))}
          </div>
          <div className="pt-2 mt-1 border-t border-[#ffffff14]">
            <p className="eyebrow text-[0.5rem] text-[#C9B79C] mb-2">Community</p>
            <div className="flex flex-col gap-3 pl-3">
              {COMMUNITY.map((a) => (
                <Link key={a.to} to={a.to} onClick={() => setOpen(false)} data-testid={`mnav-${a.label.toLowerCase()}`} className="text-[#F3EEE3]/80 text-sm">{a.label}</Link>
              ))}
            </div>
          </div>
          <div className="pt-2 mt-1 border-t border-[#ffffff14] flex flex-col gap-3">
            {LINKS_B.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-[#F3EEE3]/80 text-sm">{l.label}</Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default SiteHeader;
